/* Copyright (c) 2025 MOCA Systems, Inc. All rights reserved. */

import * as appStrings from '@touchplan/strings';
import { CommonModule } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { PresentationModule } from 'src/app/common/presentation-components/presentation.module';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { DialogReusableModule } from 'src/app/common/dialog-reusable/dialog-reusable.module';
import { MatDividerModule } from '@angular/material/divider';
import { SharedUIMaterialModule } from '../../../../shared/ui/material/shared-ui-material.module';
import { ReactiveFormsModule, FormBuilder } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { filter, last, map, takeUntil, startWith, take } from 'rxjs/operators';
import { ImportTicketsService, IBSApiRequestResults } from '../../services/import-tickets.service';
import { BehaviorSubject, Observable, Subject, combineLatest, Subscription } from 'rxjs';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DefaultLoggerService } from '@touchplan/logging';
import { LoggerServiceFactory } from 'src/app/common/logging/LoggerServiceFactory';
import { SharedUtilityComponentsModule } from 'src/app/common/utility-components/shared-utility-components.module';
import { FileItem, ImageCollectionService } from '../../services/image-collection.service';
import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { ImagePreviewDialogComponent } from '../image-preview-dialog/image-preview-dialog.component';
import { cssVar } from '@touchplan/css-var';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import { HttpErrorResponse } from '@angular/common/http';
import { BannerMessageHandler } from 'src/app/common/logging/banner-message-handler';
import { UploadOutputFormat } from 'src/app/shared/ui/material/tp-file-upload.component/tp-file-upload.component';

dayjs.extend(isoWeek);

export interface ImageCollectionDialogData {
	jobId: string;
	projectId: string;
	ticketImportService: ImportTicketsService;
	fileItems?: FileItem[];
	planStartDate?: string;
}

export interface ImageCollectionDialogResult {
	jobId?: string;
	fileItems?: FileItem[];
	planStartDate?: string;
}

enum ImageCollectionProcessingStatus {
	PROCESSING = 'processing',
	SUCCESS = 'uploaded successfully',
	FAILED = 'upload failed',
}

enum ImageCollectionUploadStatus {
	PROCESSING = 'processing',
	SUCCESS = 'uploaded successfully',
	FAILED = 'upload failed',
}

@Component({
	selector: 'image-collection',
	templateUrl: './image-collection.component.html',
	styleUrls: ['./image-collection.component.scss'],
	imports: [
		CommonModule,
		MatDialogModule,
		MatIconModule,
		MatButtonModule,
		PresentationModule,
		DialogReusableModule,
		MatDividerModule,
		SharedUIMaterialModule,
		ReactiveFormsModule,
		MatFormFieldModule,
		MatInputModule,
		MatDatepickerModule,
		MatProgressSpinnerModule,
		SharedUtilityComponentsModule,
		DragDropModule,
	],
	standalone: true,
	providers: [
		LoggerServiceFactory.provide('ImageCollectionComponent'),
		ImportTicketsService,
		ImageCollectionService,
	],
})
export class ImageCollectionComponent implements OnInit, OnDestroy {
	constructor(
		public dialogRef: MatDialogRef<ImageCollectionComponent, ImageCollectionDialogResult>,
		private formBuilder: FormBuilder,
		//neat - angular material doesn't put dialogs inside the injector the dialog was inserted from
		// so I guess we get to pass in a service instance
		// or maybe take those methods out the service (probably better)
		//public ticketImportService: ImportTicketsService,
		@Inject(MAT_DIALOG_DATA) public data: ImageCollectionDialogData,
		private logger: DefaultLoggerService,
		private imageCollectionService: ImageCollectionService,
		private dialog: MatDialog,
		private bannerMessageHandler: BannerMessageHandler
	) {
		if (data) {
			this.jobId = data.jobId;
			this.files = data.fileItems || [];
			if (data.planStartDate) {
				this.imageCollectionForm.get('planStartDate')?.setValue(data.planStartDate);
			}
		}
	}

	protected readonly appStrings = appStrings;
	protected readonly UploadOutputFormat = UploadOutputFormat;
	// TODO: centralize this and the project-info component this was copied from. Note this version and the project-info component are not the same.
	// We had to make them different here for unknown reason (tp-file-upload is always invalid with the project-info component version), resolve when combining.
	protected readonly acceptedFileTypes = [
		'image/jpeg',
		'image/jpg',
		'image/png',
		'image/heif',
		'image/heic',
		'image/webp',
	];

	protected jobId?: string = undefined;
	protected destroy$ = new Subject<void>();
	protected jobStatusSubscription: Subscription | undefined;
	protected uploadStatus$ = new BehaviorSubject<ImageCollectionUploadStatus | null>(null);
	protected processingStatus$ = new BehaviorSubject<ImageCollectionProcessingStatus | null>(null);
	protected isUploading$ = this.uploadStatus$.pipe(
		filter((status) => status !== null),
		map((status) => status === ImageCollectionUploadStatus.PROCESSING)
	);
	protected isProcessing$ = this.processingStatus$.pipe(
		filter((status) => status !== null),
		map((status) => status === ImageCollectionProcessingStatus.PROCESSING)
	);
	protected isLoading$ = combineLatest([
		this.isUploading$.pipe(startWith(false)),
		this.isProcessing$.pipe(startWith(false)),
	]).pipe(
		map(([isUploading, isProcessing]) => {
			return isUploading || isProcessing;
		})
	);

	protected statusRtdb$ = new BehaviorSubject<IBSApiRequestResults | null>(null);
	protected processingProgress$: Observable<number> = this.statusRtdb$.pipe(
		filter((status) => status !== null),
		map((status) => {
			if (typeof status?.statusStep === 'number' && typeof status?.statusMax === 'number') {
				return Math.round((status.statusStep / status.statusMax) * 100);
			}
			return 0;
		})
	);

	// This flag is used to determine if the dialog should attempt to auto-close
	// when the processing is completed without errors.
	// Example: if the user uploads images, the processing is completed,
	// and there are no errors, the dialog should close automatically.
	// Example 2: if the user uploads images, the processing is completed,
	// and there are errors, the dialog should close automatically. The user opens the dialog again, process is still completed,
	// but the dialog just got opened, so it should not auto-close.
	protected shouldAttemptToAutoClose = false;

	protected files: FileItem[] = [];

	protected imageCollectionForm = this.formBuilder.group({
		// BEGIN PLACEHOLDER REPLACE AS PART OF TP-7076
		files: this.formBuilder.control<File[]>(null),
		// END PLACEHOLDER REPLACE AS PART OF TP-7076
		planStartDate: [''],
		replaceFile: this.formBuilder.control<File>(null),
	});

	// We need to default the plan start date to the start of the current ISO week
	// We are not doing this in the form builder because we want to keep the form control optional (per design)
	// Default plan start date is the start of the current ISO week.
	private readonly defaultPlanStartDate = dayjs().startOf('isoWeek').format('YYYY-MM-DD');

	public ngOnInit(): void {
		this.listenAndProcessFilesChanges();
		this.listenAndProcessRtdbStatusChanges();
		if (this.jobId) {
			this.listenForStatus(this.jobId);
		}
	}

	private listenAndProcessFilesChanges(): void {
		this.imageCollectionForm
			.get('files')
			?.valueChanges.pipe(takeUntil(this.destroy$))
			.subscribe((files) => {
				files.forEach((file) => {
					this.files.push(
						this.imageCollectionService.createFileItem(
							file,
							this.files.length,
							this.imageCollectionForm.get('planStartDate')?.value ?? ''
						)
					);
				});
				this.files.sort((a, b) => a.order - b.order);
			});
	}

	private listenAndProcessRtdbStatusChanges(): void {
		this.statusRtdb$
			.pipe(
				takeUntil(this.destroy$),
				filter((status) => status !== null)
			)
			.subscribe((status) => {
				// Close the dialog automatically if the status is "completed"
				// and if there are no errors for any of the images
				if (status?.completedAt) {
					const errorMessage = status.errorMessage;
					if (!errorMessage && this.shouldAttemptToAutoClose) {
						this.processingStatus$.next(ImageCollectionProcessingStatus.SUCCESS);
						this.dialogRef.close({
							jobId: this.jobId,
							fileItems: this.files.map((item) => ({ ...item })),
							planStartDate: this.imageCollectionForm.get('planStartDate')?.value ?? '',
						});
					} else {
						// Update the image items with the error messages if any
						this.processingStatus$.next(ImageCollectionProcessingStatus.FAILED);
						this.files.forEach((file) => {
							const imageResult = status.images[file.id ?? ''];
							file.error = imageResult?.error || null;
						});
					}
				}
			});
	}

	private listenForStatus(jobId: string) {
		if (this.jobStatusSubscription) {
			this.jobStatusSubscription.unsubscribe();
		}
		this.jobStatusSubscription = this.imageCollectionService
			.listenForStatus(jobId)
			.pipe(takeUntil(this.destroy$))
			.subscribe(this.statusRtdb$);
	}

	protected uploadFiles() {
		// BEGIN PLACEHOLDER REPLACE AS PART OF TP-7076
		const isFilesError = this.files?.length === 0 || this.files?.length > 20;
		if (this.imageCollectionForm.invalid || isFilesError) {
			if (isFilesError) {
				this.logger.error('Invalid number of files uploaded:', this.files.length);
				throw new Error(appStrings.IMAGE_COLLECTION_INVALID_NUMBER_OF_IMAGES);
			}
			this.logger.error('Invalid form submission:', this.imageCollectionForm.errors);
			return;
		}
		// END PLACEHOLDER REPLACE AS PART OF TP-7076

		// Always generate a new jobId for each upload
		this.jobId = this.data.ticketImportService.generateBSJobId();
		this.listenForStatus(this.jobId);

		const files = this.files;
		const projectId = this.data.projectId;

		this.uploadStatus$.next(ImageCollectionUploadStatus.PROCESSING);
		this.imageCollectionService.uploadFiles(files, projectId, this.jobId).subscribe(() => {
			const anyErrors = files.some((file) => file.error);
			if (anyErrors) {
				this.uploadStatus$.next(ImageCollectionUploadStatus.FAILED);
				this.bannerMessageHandler.displayErrorMessage(new Error(appStrings.IMAGE_COLLECTION_INVALID_IMAGES));
				this.logger.error('Some files failed to upload.');
			} else {
				this.uploadStatus$.next(ImageCollectionUploadStatus.SUCCESS);
				this.startProcessing();
			}
		});
	}

	protected startProcessing(): void {
		this.shouldAttemptToAutoClose = true;
		this.processingStatus$.next(ImageCollectionProcessingStatus.PROCESSING);
		const planStartDate = this.imageCollectionForm.get('planStartDate')?.value || this.defaultPlanStartDate;
		this.data.ticketImportService
			.startBSImageProcessing(this.data.projectId, this.jobId, { planStartDate })
			.pipe(last())
			.subscribe({
				error: (response: HttpErrorResponse) => {
					this.logger.error('Error on image processing:', response);
					this.processingStatus$.next(ImageCollectionProcessingStatus.FAILED);
					this.bannerMessageHandler.displayErrorMessage(response, response.error?.error);
				},
			});
		// setTimeout(() => {
		// 	// Simulate a 50% progress for testing purposes
		// 	this.simulateFiftyPercent();
		// }, 2000); // Adjust the delay as needed for testing
	}

	protected doReorder(event: CdkDragDrop<FileItem[]>) {
		moveItemInArray(this.files, event.previousIndex, event.currentIndex);
		// Update the order of the files after reordering
		this.files = this.imageCollectionService.orderFiles(this.files);
	}

	protected previewFile(file: FileItem): void {
		this.dialog.open(ImagePreviewDialogComponent, {
			width: cssVar.dialogWidthXl,
			// We need to set the height to a specific value to avoid the dialog and image being too large.
			height: '80vh',
			data: {
				imageUrl: file.thumbnailUrl,
				altText: file.name,
			},
		});
	}

	protected replaceFile(file: FileItem, fileInput: HTMLInputElement): void {
		// Fake click on the file input to trigger file selection
		fileInput.click();
		this.imageCollectionForm
			.get('replaceFile')
			?.valueChanges.pipe(
				take(1),
				filter((newFile) => newFile !== null)
			)
			.subscribe((newFile: File) => {
				// Create a new FileItem with the new file
				const newFileItem = this.imageCollectionService.createFileItem(
					newFile,
					file.order,
					this.imageCollectionForm.get('planStartDate')?.value ?? ''
				);
				// Replace the old file item with the new one
				const index = this.files.findIndex((f) => f.order === file.order);
				if (index !== -1) {
					this.files[index] = newFileItem;
					this.files = this.imageCollectionService.orderFiles(this.files);
				}
			});
	}

	protected replaceFileInputChange(event: Event): void {
		const input = event.target as HTMLInputElement;
		if (input.files && input.files.length > 0) {
			const newFile = input.files[0];
			this.imageCollectionForm.get('replaceFile')?.setValue(newFile);
			input.value = '';
		}
	}

	protected removeFile(file: FileItem): void {
		const index = this.files.findIndex((f) => f.order === file.order);
		if (index !== -1) {
			this.files.splice(index, 1);
			this.files = this.imageCollectionService.orderFiles(this.files);
		}
	}

	public ngOnDestroy(): void {
		this.uploadStatus$.complete();
		this.processingStatus$.complete();
		this.statusRtdb$.complete();

		this.destroy$.next();
		this.destroy$.complete();
	}

	// protected simulateCompleted(): void {
	// 	// Simulate a completed status for testing purposes
	// 	const simulatedStatus: Partial<IBSApiRequestResults> = {
	// 		statusMessage: 'Simulated completed status',
	// 		startedAt: new Date().toISOString(),
	// 		statusMax: this.files.length,
	// 		statusStep: this.files.length,
	// 		completedAt: new Date().toISOString(),
	// 		errorMessage: null,
	// 		statusAt: new Date().toISOString(),
	// 	};

	// 	const bsPath = fancyFirebaseGetters.getApiRequestsPath(this.jobId);
	// 	const ref = fancyFirebaseReferencer.getRef(bsPath);
	// 	const childRef = fancyDatabase.child(ref, 'results');
	// 	fancyDatabase
	// 		.update(childRef, simulatedStatus)
	// 		.then(() => {
	// 			console.log('Simulated completed status set successfully');
	// 		})
	// 		.catch((error) => {
	// 			console.error('Error setting simulated completed status:', error);
	// 		});
	// }

	// protected simulateFailed(): void {
	// 	// Simulate a failed status for testing purposes
	// 	const simulatedStatus: Partial<IBSApiRequestResults> = {
	// 		statusMessage: 'Simulated failed status',
	// 		startedAt: new Date().toISOString(),
	// 		statusMax: this.files.length,
	// 		statusStep: this.files.length,
	// 		completedAt: new Date().toISOString(),
	// 		errorMessage: 'Simulated error message',
	// 		statusAt: new Date().toISOString(),
	// 	};

	// 	const bsPath = fancyFirebaseGetters.getApiRequestsPath(this.jobId);
	// 	const ref = fancyFirebaseReferencer.getRef(bsPath);
	// 	const childRef = fancyDatabase.child(ref, 'results');
	// 	fancyDatabase
	// 		.update(childRef, simulatedStatus)
	// 		.then(() => {
	// 			console.log('Simulated failed status set successfully');
	// 		})
	// 		.catch((error) => {
	// 			console.error('Error setting simulated failed status:', error);
	// 		});
	// }

	// protected simulateSingleFileFailed(): void {
	// 	// Simulate a single file upload failure for testing purposes
	// 	const simulatedStatus: Partial<IBSApiRequestResults> = {
	// 		statusMessage: 'Simulated single file failed status',
	// 		startedAt: new Date().toISOString(),
	// 		statusMax: this.files.length,
	// 		statusStep: this.files.length,
	// 		completedAt: new Date().toISOString(),
	// 		errorMessage: 'See individual file errors',
	// 		statusAt: new Date().toISOString(),
	// 		images: {
	// 			[this.files[0].id]: {
	// 				imageUrl: null,
	// 				order: 0,
	// 				uploadedAt: new Date().toISOString(),
	// 				error: 'Simulated single file upload error',
	// 			},
	// 		},
	// 	};
	// 	const bsPath = fancyFirebaseGetters.getApiRequestsPath(this.jobId);
	// 	const ref = fancyFirebaseReferencer.getRef(bsPath);
	// 	const childRef = fancyDatabase.child(ref, 'results');
	// 	fancyDatabase
	// 		.update(childRef, simulatedStatus)
	// 		.then(() => {
	// 			console.log('Simulated single file failed status set successfully');
	// 		})
	// 		.catch((error) => {
	// 			console.error('Error setting simulated single file failed status:', error);
	// 		});
	// }

	// protected simulateIsUploading(): void {
	// 	this.uploadStatus$.next(ImageCollectionUploadStatus.PROCESSING);
	// }

	// protected simulateIsProcessing(): void {
	// 	this.processingStatus$.next(ImageCollectionProcessingStatus.PROCESSING);
	// }

	// protected simulateFiftyPercent(): void {
	// 	// Simulate a 50% progress for testing purposes
	// 	const simulatedStatus: Partial<IBSApiRequestResults> = {
	// 		statusMessage: 'Simulated 50% progress',
	// 		startedAt: new Date().toISOString(),
	// 		statusMax: this.files.length,
	// 		statusStep: Math.floor(this.files.length / 2),
	// 		completedAt: null,
	// 		errorMessage: null,
	// 		statusAt: new Date().toISOString(),
	// 	};

	// 	const bsPath = fancyFirebaseGetters.getApiRequestsPath(this.jobId);
	// 	const ref = fancyFirebaseReferencer.getRef(bsPath);
	// 	const childRef = fancyDatabase.child(ref, 'results');
	// 	fancyDatabase
	// 		.update(childRef, simulatedStatus)
	// 		.then(() => {
	// 			console.log('Simulated 50% progress set successfully');
	// 		})
	// 		.catch((error) => {
	// 			console.error('Error setting simulated 50% progress:', error);
	// 		});
	// }
}
